import { ReactElement, ReactNode } from 'react'
import MuiRadio, { RadioProps as MuiRadioProps } from '@mui/material/Radio'
import { Field, FieldProps, useField } from 'react-final-form'
import {
  Divider,
  FormControl,
  FormControlLabel,
  FormControlLabelProps,
  FormControlProps,
  FormGroup,
  FormGroupProps,
  FormHelperText,
  FormHelperTextProps,
  FormLabel,
  FormLabelProps,
  Tooltip,
} from '@mui/material'
import styled, { css } from 'styled-components'
import { TYPOGRAPHY_MIXIN } from 'src/shared/constants/styling-constants'
import { InfoIcon } from 'src/shared/icons/Icons'
import { useMessageSource } from 'src/i18n/useMessageSource'

export interface RadioData {
  label?: string
  value?: unknown
  disabled?: boolean
  tooltip?: string
}

export interface RadioButtonProps extends Partial<Omit<MuiRadioProps, 'onChange'>> {
  name: string
  data: RadioData | RadioData[]
  label?: ReactNode
  required?: boolean
  helperText?: string
  fieldProps?: Partial<FieldProps<any, any>>
  formControlProps?: Partial<FormControlProps>
  formGroupProps?: Partial<FormGroupProps>
  formLabelProps?: Partial<FormLabelProps>
  formControlLabelProps?: Partial<FormControlLabelProps>
  formHelperTextProps?: Partial<FormHelperTextProps>
  validate?: any
  render?: (option: RadioData, index: number) => ReactElement
  withDividersBetween?: boolean
  $withTextField?: boolean
  $noLabel?: boolean
}

const config = {
  subscription: {
    error: true,
    submitError: true,
    dirtySinceLastSubmit: true,
    touched: true,
    modified: true,
  },
}

const FormLabelStyled = styled(FormLabel)`
  padding: ${({ theme }) => theme.spacing(1.5)} ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.colors.text.primary};
  ${TYPOGRAPHY_MIXIN.subtitle2};
`

const DivStyled = styled('div')<{ $disabled?: boolean; $withTextField?: boolean; $noLabel?: boolean }>`
  padding: 0 ${({ theme }) => theme.spacing(2)};
  display: flex;
  align-items: center;
  gap: ${({ $withTextField, theme }) => ($withTextField ? 0 : theme.spacing(1))};
  &:hover {
    background-color: ${({ $disabled, theme }) => ($disabled ? 'none' : theme.colors.action.hover)};
  }
  ${({ $withTextField }) =>
    $withTextField &&
    css`
      align-items: flex-start;
      & > .MuiFormControlLabel-root {
        height: 3.5rem;
      }
      & > .MuiSvgIcon-root {
        position: relative;
        top: 1rem;
      }
      &:hover {
        background-color: transparent;
      }
      label {
        cursor: default;
      }
    `}
  ${({ $noLabel }) =>
    $noLabel &&
    css`
      display: inline-block;
      &:hover {
        background-color: transparent;
      }
      & > .MuiFormControlLabel-root {
        margin-left: 0;
        cursor: default;
      }
    `}
`

const FormControlLabelStyled = styled(FormControlLabel)`
  height: 3rem;
  margin-right: 0;
  flex-grow: 1;
`

const InfoIconStyled = styled(InfoIcon)`
  font-size: 1.25rem;
`

const DividerStyled = styled(Divider)(({ theme }) => ({
  height: '2px',
  backgroundColor: theme.palette.divider,
}))

export const RadioButtonGroupField = (props: RadioButtonProps): ReactElement => {
  const {
    required,
    label,
    data,
    name,
    helperText,
    fieldProps,
    formControlProps,
    formGroupProps,
    formLabelProps,
    formControlLabelProps,
    formHelperTextProps,
    validate,
    render,
    withDividersBetween,
    $withTextField,
    $noLabel,
    ...rest
  } = props

  const itemsData = Array.isArray(data) ? data : [data]
  const single = !Array.isArray(data)
  const field = useField(name, config)
  const { touched, error: errorObject } = field.meta
  const { getMessage } = useMessageSource()
  const error = errorObject && getMessage(errorObject.errorKey, errorObject.params)
  const invalid = Boolean(touched && error)

  return (
    <FormControl required={required} error={invalid} {...formControlProps} fullWidth>
      {label ? <FormLabelStyled {...formLabelProps}>{label}</FormLabelStyled> : <></>}
      <FormGroup {...formGroupProps}>
        {itemsData.map((item: RadioData, idx: number) => (
          <div key={idx}>
            <DivStyled
              key={idx}
              $disabled={item.disabled || rest.disabled}
              $withTextField={$withTextField}
              $noLabel={$noLabel}
            >
              <FormControlLabelStyled
                key={idx}
                name={name}
                label={item.label || ''}
                value={single ? undefined : item.value}
                disabled={item.disabled || rest.disabled}
                control={
                  <Field
                    type="radio"
                    name={name}
                    validate={validate}
                    render={({ input: { name, value, onChange, checked, ...restInput } }) => (
                      <MuiRadio
                        name={name}
                        value={value}
                        onChange={onChange}
                        checked={checked}
                        disabled={item.disabled || rest.disabled}
                        inputProps={{ required, ...restInput }}
                        {...rest}
                      />
                    )}
                    {...fieldProps}
                  />
                }
                {...formControlLabelProps}
              />
              {item.tooltip && (
                <Tooltip title={item.tooltip} placement="right">
                  <InfoIconStyled />
                </Tooltip>
              )}
              {render && render(item, idx)}
            </DivStyled>
            {withDividersBetween && <DividerStyled />}
          </div>
        ))}
      </FormGroup>
      <FormHelperText error sx={{ ...($noLabel && { display: 'none' }) }} {...formHelperTextProps}>
        {(invalid && error) || ' '}
      </FormHelperText>
    </FormControl>
  )
}
